import { createSearchParams, useNavigate } from 'react-router-dom'

import { Box, List, Spinner, Table } from '@factorialco/deprecated-design-system'
import { sortBy } from 'lodash'

import Select from 'modules/core/components/Select'
import createMemo from 'modules/core/hooks/createMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import Body from 'modules/bookkeepers/components/TableComponents/Body'
import Header from 'modules/bookkeepers/components/TableComponents/Header'
import { IncidenceTaskTypesProvider } from 'modules/bookkeepers/hooks/TaskTypesContext'
import useGetIncidenceTaskTypes from 'modules/bookkeepers/hooks/useGetIncidenceTaskTypes'
import type { IFilters } from 'modules/bookkeepers/hooks/useIncidenceFilters'
import useIncidenceTypeQueryParam from 'modules/bookkeepers/hooks/useIncidenceTypeQueryParam'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { trackIncidenceFilter } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { FilterOption, statusOptions } from 'modules/bookkeepers/lib/filterOptions'
import {
  getIncidenceTypeNavigationMapping,
  incidenceTypeNavigation,
  showIncidenceTypeColumn,
} from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import { useGetMarketplaceIntegrations } from 'modules/marketplace/hooks/integrations/useGetMarketplaceIntegrations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'

import SearchBar from './SearchBar'

const useLegalEntityOptions = createMemo((selectedCompany) => {
  const legalEntitiesSource: LegalEntity[] =
    selectedCompany?.legalEntities ?? legalEntities.toArray()
  const sortedLegalEntities = sortBy(legalEntitiesSource, (model) =>
    model.get('legal_name').toLowerCase()
  )

  const formLegalEntities = sortedLegalEntities.map((model) => {
    return {
      label: String(model.attributes.legal_name),
      value: model.attributes.id ?? '',
    }
  })

  const allOptions = {
    label: i18n.t('bookkeepers.incidence.filters.legal_entity'),
    value: legalEntitiesSource.map((le) => le.id).join(','),
  }

  return [formLegalEntities.length > 1 ? allOptions : [], ...formLegalEntities].flat()
})

interface Props {
  loading: boolean
  selectedIncidence: string | null | undefined
}

const IncidencesList = ({ selectedIncidence, loading }: Props) => {
  const { incidenceType } = useIncidenceTypeQueryParam()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const isBookkeeperView = useBookkeeperView()
  const { hasInhousePayroll } = useInhousePayroll()
  const { filters, onFilterChange, search, onSearch, selectedCompany } = useIncidenceFilterContext()
  const searchParams = createSearchParams({ incidenceType })
  const showIncidenceType = !incidenceType || showIncidenceTypeColumn.includes(incidenceType)

  const incidencesRoute = incidenceType
    ? `${navigation.bookkeepers.incidences.fullPath()}?${searchParams}`
    : navigation.bookkeepers.incidences.fullPath()

  const handleRowSelectPath = (id: string, isReopened: boolean) => {
    if (incidenceType === incidenceTypeNavigation.ALL_WITH_MESSAGES) {
      return `${navigation.bookkeepers.incidences.idMessages.fullPath({
        id: id,
      })}?${searchParams}`
    }

    const path = isReopened
      ? navigation.bookkeepers.incidences.idChanges.fullPath({ id })
      : navigation.bookkeepers.incidences.details.fullPath({ id })

    if (incidenceType) {
      return `${path}?${searchParams}`
    }
    return path
  }

  const legalEntitiesOptions: FilterOption[] = useLegalEntityOptions(selectedCompany)

  const handleFilterChange = (key: keyof IFilters, value: string) => {
    onFilterChange(key, value)
    trackFilter(key, value)
    navigate(incidencesRoute)
  }

  const parseFilterChange = (value: undefined | string) => {
    return !value ? '' : value
  }

  const trackFilter = (filterType: keyof IFilters, value: string) => {
    trackIncidenceFilter(filterType, value, isBookkeeperView, hasInhousePayroll)
  }

  const { loading: fetchingTypes, taskTypes } = useGetIncidenceTaskTypes()

  const { data: integrationsData, loading: loadingIntegration } = useGetMarketplaceIntegrations({
    datoIds: [integrationCodes.a3innuva],
  })

  const integration = integrationsData.at(0)
  const integrationId = integration?.id
  const { data: companyInstallations } = useGetMarketplaceCompanyInstallations(
    {
      // FIXME: We should be merging this query and the former one into a single one
      integrationIds: [integrationId ?? '0'],
    },
    false
  )
  const showIncidencePayrollIntegrationStatus = () => {
    if (companyInstallations.length > 0) {
      const mapping = getIncidenceTypeNavigationMapping()

      const hirings = mapping[incidenceTypeNavigation.NEW_HIRES]?.[0]
      const terminations = mapping[incidenceTypeNavigation.TERMINATIONS]?.[0]
      return filters.name === hirings || filters.name === terminations
    } else {
      return false
    }
  }

  return (
    <IncidenceTaskTypesProvider value={taskTypes}>
      <Box width="full">
        <List>
          <Box
            width="full"
            flexDirection="row"
            background="grey200"
            alignItems="center"
            paddingX="s8"
            paddingY="s6"
            gap="s12"
          >
            <Box width="s240">
              <Select
                options={statusOptions(isBookkeeperView)}
                value={parseFilterChange(filters.status)}
                onChange={(value: string) => handleFilterChange('status', value)}
              />
            </Box>
            <Box width="s240">
              <Select
                options={legalEntitiesOptions}
                value={parseFilterChange(filters.legal_entity_id)}
                onChange={(value: string) => handleFilterChange('legal_entity_id', value)}
              />
            </Box>
            <Box width="full">
              <SearchBar search={search} onSearch={onSearch} />
            </Box>
          </Box>
          {fetchingTypes || loadingIntegration ? (
            <Box alignItems="center">
              <Spinner />
            </Box>
          ) : (
            <Table
              density="compact"
              autoLayout
              fullWidth
              noTopRadius
              hideBorder
              background="grey000White"
            >
              <Header.Bookkeeper
                showIncidenceTypeColumn={showIncidenceType}
                showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus()}
              />
              <Body
                selectedIncidence={selectedIncidence}
                loading={loading}
                onRowSelectPath={handleRowSelectPath}
                showIncidenceTypeColumn={showIncidenceType}
                showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus()}
              />
            </Table>
          )}
        </List>
      </Box>
    </IncidenceTaskTypesProvider>
  )
}

export default IncidencesList
